import React from 'react';
import type {Dispatch, RefObject, SetStateAction} from 'react';

import {Form, H3, P} from '@storybook/components';
import {styled} from '@storybook/theming';

import {useNewIssueForm} from './hooks/useNewIssue';

interface ComponentProps {
    forwardedRef: RefObject<HTMLDialogElement | null>;
    setUpdated: Dispatch<SetStateAction<number>>;
    storyId: string | null;
}

/**
 * NewIssue.
 *
 * @param props              Component props.
 * @param props.forwardedRef The ref.
 * @param props.storyId      The story id.
 * @param props.setUpdated   The set updated.
 * @returns The component.
 */
const NewIssue = ({forwardedRef, setUpdated, storyId}: ComponentProps) => {
    const {body, error, handleBody, handleNewIssue, handleTitle, title} = useNewIssueForm({
        forwardedRef,
        setUpdated,
        storyId
    });

    /**
     * Handle close.
     */
    const handleClose = () => {
        forwardedRef.current?.close();
    };

    return (
        <Wrapper ref={forwardedRef}>
            <Close onClick={handleClose} />
            <FlexWrapper>
                <StyledH3>Create new Issue</StyledH3>
                <Form.Field label="Title">
                    <Form.Input id="title" size="100%" value={title} onChange={handleTitle} />
                </Form.Field>
                <Form.Field label="Description">
                    <Form.Textarea id="body" size="100%" value={body} onChange={handleBody} />
                </Form.Field>
                <Spacing />
                {error && <Error>{error}</Error>}
                <Form.Button variant="solid" onClick={handleNewIssue}>Submit new Issue</Form.Button>
            </FlexWrapper>
        </Wrapper>
    );
};

NewIssue.displayName = 'NewIssue';

export default NewIssue;

const Wrapper = styled.dialog`
    border: 1px solid #c3c3c3;
    border-radius: 5px;
    height: 70%;
    width: 60%;

    @media (prefers-color-scheme: dark) {
        background-color: #1B1C1D;
        color: #C9CDCF;
    }
    @media (prefers-color-scheme: light) {
        background-color: #fff;
        color: #333333;
    }
`;

const Close = styled.button`
    align-items: center;
    all: unset;
    cursor: pointer;
    display: flex;
    height: 20px;
    justify-content: center;
    position: absolute;
    right: 10px;
    top: 10px;
    width: 20px;
    z-index: 20;

    &:before {
        content: 'X';
        cursor: pointer;
        font-size: 20px;
        font-weight: bold;

        @media (prefers-color-scheme: dark) {
            color: #C9CDCF;
        }
        @media (prefers-color-scheme: light) {
            color: #333333;
        }
    }
`;

const StyledH3 = styled(H3)`
    text-align: center;
`;

const FlexWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const Spacing = styled.div`
    flex-grow: 1;
`;

const Error = styled(P)`
    color: #FF4400;
`;